import { IMAGES_BASE_URL } from "@/lib/config";
import { capitalizeCityName } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "../ui/fallbackImage";
import type { Course } from "@/services/golfClubService";
import { IonIcon } from "@ionic/react";
import { heartOutline, locationOutline } from "ionicons/icons";

type FacilityCardItemProps = {
  course: Course;
  onSelect: () => void;
  showCourseCount?: boolean;
  showDistance?: boolean;
};

const FacilityCardItem = ({
  course,
  onSelect,
  showCourseCount = true,
  showDistance = true,
}: FacilityCardItemProps) => {
  const { t } = useTranslation('MGP');

  return (
    <div
      className="overflow-hidden cursor-pointe"
      onClick={onSelect}
    >
      {/* Image with favorite button */}
      <div className="relative aspect-square w-full p-2">
        <ImageWithFallback
          alt={`${course.name} photo`}
          fallbackImage="/assets/images/course_fallback.webp"
          src={`${IMAGES_BASE_URL}/${course.id}.jpg`}
          className="size-full rounded-xl object-cover"
        />
        <button
          className="absolute top-2 right-2 bg-white/80 hover:bg-white p-2 rounded-full transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            // Handle favorite
          }}
        >
          <IonIcon
            icon={heartOutline}
            className="text-gray-700 hover:text-rose-500 text-lg"
          />
        </button>
      </div>

      {/* Card content */}
      <div className="p-2">
        <div className="flex justify-between items-start">
          <h3 className="font-medium line-clamp-2">
            {capitalizeCityName(course.name)}
          </h3>
        </div>

        <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
          <IonIcon icon={locationOutline} className="mr-1" />
          <span className="line-clamp-1">
            {capitalizeCityName(course.city)}, 
            <span 
              className="text-primary cursor-pointer hover:underline" 
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to country view
                window.location.href = `/s/${encodeURIComponent(course.country)}`;
              }}
            >
              {course.country}
            </span>
            {course.state && (
              <>
                , <span 
                  className="text-primary cursor-pointer hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate to state view within country
                    window.location.href = `/s/${encodeURIComponent(course.country)}/${encodeURIComponent(course.state || '')}`;
                  }}
                >
                  {course.state}
                </span>
              </>
            )}
          </span>
        </div>

        <div className="mt-2 flex items-center justify-between">
          {showDistance && course.distance && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {course.distance} km
            </div>
          )}
        </div>

        {showCourseCount && course.courses && course.courses.length > 1 && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {course.courses.length} {t('courses')}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilityCardItem;
